import type { IBlock } from '@/types/block';
import type { IBlockCache, IBlockCacheDebugInfo } from './types';
import type { IMapQueue } from '@/classes/sizeLimitedFifoMapQueue/types';

import { SizeLimitedFifoMapQueue } from '@/classes/sizeLimitedFifoMapQueue';
import { Block } from '@/classes/Block';

// Week consists of 336 blocks. This cache can potentially store almost a month's worth of blocks.
export const BLOCK_CACHE_SIZE_LIMIT = 336 * 4;

/**
 * Write-through in memory cache for Blocks.
 * Stores block in one timezone only, changing timezone results in cache invalidation.
 */
export class BlockCache implements IBlockCache {
  private static _instance: BlockCache;
  private static _timezone: string;

  private _blocks: IMapQueue<number, IBlock>;

  private constructor(timezone: string) {
    BlockCache._timezone = timezone;
    this._blocks = new SizeLimitedFifoMapQueue<number, IBlock>(BLOCK_CACHE_SIZE_LIMIT);
  }

  static getInstance(timezone: string): IBlockCache {
    // change to the requested timezone invalidates cache
    if (!BlockCache._instance || BlockCache._timezone !== timezone) {
      BlockCache._instance = new BlockCache(timezone);
    }

    return BlockCache._instance;
  }

  static debug(): IBlockCacheDebugInfo {
    return { timezone: BlockCache._timezone, blocks: BlockCache._instance?._blocks };
  }

  setSizeLimit(size: number): void {
    this._blocks.sizeLimit = size;
  }

  getBlock(blockId: number): IBlock {
    const block = this._blocks.get(blockId);

    if (block) return block;

    const newBlock = Block.fromBlockId(blockId, BlockCache._timezone);
    this._blocks.set(blockId, newBlock);

    return newBlock;
  }
}
